// Analytics.jsx
// Dashboard with flexible periods, topline engagement, and retention cohorts

import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  Timestamp,
} from "firebase/firestore";
import db from "../../Firebase";

import ConsoleHomePageHeader from "../Home/ConsoleHomePageHeader";
import SimpleBarChart from "./SimpleBarChart";
import "./Analytics.css";

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

const monthDiff = (start, date) =>
  (date.getFullYear() - start.getFullYear()) * 12 + (date.getMonth() - start.getMonth());

const BUCKETS = {
  day: {
    label: "Daily",
    buckets: 30,
    start: () => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - 29);
      return d;
    },
    index: (start, date) => Math.floor((date.getTime() - start.getTime()) / DAY_MS),
    labelFor: (start, idx) => {
      const d = new Date(start);
      d.setDate(d.getDate() + idx);
      return `${d.getMonth() + 1}/${d.getDate()}`;
    },
  },
  week: {
    label: "Weekly",
    buckets: 12,
    start: () => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      const dow = d.getDay();
      d.setDate(d.getDate() - dow - (12 - 1) * 7);
      return d;
    },
    index: (start, date) => Math.floor((date.getTime() - start.getTime()) / WEEK_MS),
    labelFor: (start, idx) => {
      const d = new Date(start);
      d.setDate(d.getDate() + idx * 7);
      return `${d.getMonth() + 1}/${d.getDate()}`;
    },
  },
  month: {
    label: "Monthly",
    buckets: 12,
    start: () => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(1);
      d.setMonth(d.getMonth() - (12 - 1));
      return d;
    },
    index: (start, date) => monthDiff(start, date),
    labelFor: (start, idx) => {
      const d = new Date(start);
      d.setMonth(d.getMonth() + idx);
      return `${d.getMonth() + 1}/${String(d.getFullYear()).slice(-2)}`;
    },
  },
};

const buildLabels = (startDate, config) =>
  Array.from({ length: config.buckets }, (_, idx) => config.labelFor(startDate, idx));

const parseDateFromAny = (input) => {
  if (!input) return null;
  try {
    if (input.toDate) return input.toDate();
    if (typeof input === "object" && input.seconds != null) return new Date(input.seconds * 1000);
    if (typeof input === "number") return new Date(input);
    if (typeof input === "string") {
      const parsed = Date.parse(input);
      if (!isNaN(parsed)) return new Date(parsed);
    }
  } catch (err) {
    return null;
  }
  return null;
};

const RetentionTable = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="retention-empty">Not enough retention data yet.</div>;
  }

  const weeks = data[0]?.weeks?.length || 0;
  const gridTemplate = `140px 70px ${Array.from({ length: weeks })
    .map(() => "70px")
    .join(" ")}`;

  const cellColor = (pct) => {
    if (!pct) return "#f6f8fb";
    const ratio = Math.min(1, pct / 50); // brighten up to 50%+
    const alpha = 0.15 + ratio * 0.6;
    return `rgba(76, 175, 80, ${alpha.toFixed(2)})`;
  };

  return (
    <div className="retention-table">
      <div className="retention-row retention-head" style={{ gridTemplateColumns: gridTemplate }}>
        <div>Cohort</div>
        <div>Size</div>
        {Array.from({ length: weeks }).map((_, idx) => (
          <div key={idx}>W{idx}</div>
        ))}
      </div>
      {data.map((row) => (
        <div
          className="retention-row"
          style={{ gridTemplateColumns: gridTemplate }}
          key={row.cohortLabel}
        >
          <div>{row.cohortLabel}</div>
          <div>{row.size}</div>
          {row.weeks.map((pct, idx) => (
            <div key={idx} style={{ background: cellColor(pct) }}>
              {pct ? `${pct}%` : "-"}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const InfoTooltip = ({ text }) => (
  <span className="info-tooltip" role="note" aria-label={text}>
    ?
    <span className="info-tooltip__bubble">{text}</span>
  </span>
);

const API_BASE = "https://us-central1-plants-5f51e.cloudfunctions.net/api";

const Analytics = () => {
  const [filterId, setFilterId] = useState("");
  const [period, setPeriod] = useState("week");
  const [wateringData, setWateringData] = useState([]);
  const [watererData, setWatererData] = useState([]);
  const [chatData, setChatData] = useState([]);
  const [plantData, setPlantData] = useState([]);
  const [retentionData, setRetentionData] = useState([]);
  const [recentChatters, setRecentChatters] = useState([]);
  const [recentAvgDaysActive, setRecentAvgDaysActive] = useState(null);
  const [backfillState, setBackfillState] = useState({
    running: false,
    message: "",
    updated: 0,
    skipped: 0,
    failed: 0,
  });
  const [backfillUserId, setBackfillUserId] = useState("");
  const [recentSort, setRecentSort] = useState("recent");
  const [recentAvgDaysActiveWithPlants, setRecentAvgDaysActiveWithPlants] = useState(null);
  const [createdAfter, setCreatedAfter] = useState("");
  const [createdBefore, setCreatedBefore] = useState("");
  const [activeSinceDays, setActiveSinceDays] = useState("");
  const [excludeNoPlants, setExcludeNoPlants] = useState(false);
  const [cohortMonth, setCohortMonth] = useState("");
  const [cohortWindowDays, setCohortWindowDays] = useState(14);
  const [cohortStat, setCohortStat] = useState(null);
  const [usersCache, setUsersCache] = useState([]);
  const [topline, setTopline] = useState({
    dau: 0,
    wau: 0,
    mau: 0,
    stickiness: 0,
    weeklyAvgActions: 0,
  });
  const [permError, setPermError] = useState({
    waterings: false,
    chats: false,
    plants: false,
    activity: false,
  });
  const [totals, setTotals] = useState({ waterings: null });

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterId, period]);

  const fetchAll = async () => {
    const config = BUCKETS[period] || BUCKETS.week;
    const startDate = config.start();
    const startTimestamp = Timestamp.fromDate(startDate);

    setPermError({ waterings: false, chats: false, plants: false, activity: false });

    const usersSnap = await loadUsers();
    if (usersSnap.error === "permission-denied") {
      setPermError((p) => ({ ...p, activity: true, waterings: true, chats: true, plants: true }));
      return;
    }
    const users = usersSnap.docs;

    // Waterings volume by event time
    const wateringsCounts = await getBucketedEventCounts(
      "Waterings",
      ["createdAt"],
      startDate,
      startTimestamp,
      config
    );
    if (wateringsCounts.error === "permission-denied")
      setPermError((p) => ({ ...p, waterings: true }));
    setWateringData(wateringsCounts.data);

    // Unique waterers by event userId
    const waterers = await getBucketedUniqueEvents(
      "Waterings",
      ["createdAt"],
      startDate,
      startTimestamp,
      config,
      ["userId", "uid", "authorId", "ownerId", "createdBy"]
    );
    if (waterers.error === "permission-denied") setPermError((p) => ({ ...p, waterings: true }));
    setWatererData(waterers.data);

    // Unique chatters by event userId
    const chatters = await getBucketedUniqueEvents(
      "Chats",
      ["createdAt"],
      startDate,
      startTimestamp,
      config,
      ["userId", "uid", "authorId", "ownerId", "createdBy"]
    );
    if (chatters.error === "permission-denied") setPermError((p) => ({ ...p, chats: true }));
    setChatData(chatters.data);

    setPlantData(bucketUsersByField(users, startDate, config, ["createdAt"]));

    setUsersCache(users);
    recomputeDerivedFromUsers(users);

    const total = await countAllWaterings();
    setTotals((t) => ({ ...t, waterings: total }));
  };

  const loadUsers = async () => {
    try {
      const snap = await getDocs(query(collection(db, "Users"), limit(2000)));
      return { docs: snap.docs };
    } catch (err) {
      if (err?.code === "permission-denied") {
        console.warn("No read access to Users:", err);
        return { docs: [], error: "permission-denied" };
      }
      console.error("Failed to read Users", err);
      return { docs: [] };
    }
  };

  const recomputeDerivedFromUsers = (users) => {
    const filtered = applyUserFilters(users);
    const activity = computeActiveUsersFromUsers(filtered);
    setTopline(activity);

    const retention = computeRetentionFromUsers(filtered);
    setRetentionData(retention);

    const { rows: recent, avgDaysActive, avgDaysActiveWithPlants } = computeRecentActive(filtered);
    setRecentChatters(recent);
    setRecentAvgDaysActive(avgDaysActive);
    setRecentAvgDaysActiveWithPlants(avgDaysActiveWithPlants);

    const cohort = computeCohortStat(filtered);
    setCohortStat(cohort);
  };

  const applyUserFilters = (users) => {
    const after = createdAfter ? new Date(createdAfter) : null;
    const before = createdBefore ? new Date(createdBefore) : null;
    const activeCutoff =
      activeSinceDays && Number(activeSinceDays) > 0
        ? Date.now() - Number(activeSinceDays) * DAY_MS
        : null;

    return users.filter((snap) => {
      const data = snap.data();
      if (shouldFilterOut(data, snap.id)) return false;

      const created = parseDateFromAny(data.createdAt);
      if (!created || isNaN(created.getTime())) return false;
      if (after && created.getTime() < after.setHours(0, 0, 0, 0)) return false;
      if (before && created.getTime() > before.setHours(23, 59, 59, 999)) return false;

      const lastChat = parseDateFromAny(data.lastChatAt);
      const lastWater = parseDateFromAny(data.lastWateringAt);
      const lastActive =
        lastChat && lastWater
          ? new Date(Math.max(lastChat.getTime(), lastWater.getTime()))
          : lastChat || lastWater;
      if (activeCutoff && (!lastActive || lastActive.getTime() < activeCutoff)) return false;

      const hasPlants = Array.isArray(data.ownedPlants) && data.ownedPlants.length > 0;
      if (excludeNoPlants && !hasPlants) return false;

      return true;
    });
  };

  const shouldFilterOut = (data, snapId) => {
    const f = (filterId || "").trim();
    return (
      !!f &&
      (data.plantId === f ||
        data.plantID === f ||
        data.id === f ||
        snapId === f ||
        data.userId === f ||
        data.uid === f)
    );
  };

  const getBucketedEventCounts = async (
    collectionName,
    candidateFields,
    startDate,
    startTimestamp,
    config
  ) => {
    const labels = buildLabels(startDate, config);
    const toChart = (docs, chosenField) => {
      const counts = Array(config.buckets).fill(0);
      docs.forEach((snap) => {
        const data = snap.data();
        if (shouldFilterOut(data, snap.id)) return;
        const field = chosenField || candidateFields.find((k) => data[k] != null);
        if (!field) return;
        const d = parseDateFromAny(data[field]);
        if (!d || isNaN(d.getTime())) return;
        const idx = config.index(startDate, d);
        if (idx >= 0 && idx < config.buckets) counts[idx]++;
      });
      return labels.map((label, idx) => ({ label, value: counts[idx] }));
    };

    for (const field of candidateFields) {
      try {
        const q = query(collection(db, collectionName), where(field, ">=", startTimestamp));
        const snapshot = await getDocs(q);
        console.log("bucketed events fetched", {
          collectionName,
          field,
          count: snapshot.size,
          sample: snapshot.docs[0]?.data?.()[field],
        });
        return { data: toChart(snapshot.docs, field) };
      } catch (err) {
        if (err?.code === "permission-denied") {
          console.warn(`No read access to ${collectionName}:`, err);
          return { data: toChart([], null), error: "permission-denied" };
        }
        console.error("Index needed?", collectionName, field, err);
      }
    }

    try {
      const snapshot = await getDocs(query(collection(db, collectionName), limit(2000)));
      console.log("bucketed events fallback", {
        collectionName,
        count: snapshot.size,
        sample: snapshot.docs[0]?.data?.()[candidateFields[0]],
      });
      return { data: toChart(snapshot.docs, null) };
    } catch (err) {
      if (err?.code === "permission-denied") {
        console.warn(`No read access to ${collectionName}:`, err);
        return { data: toChart([], null), error: "permission-denied" };
      }
      console.error(`Failed to read ${collectionName}`, err);
      return { data: toChart([], null) };
    }
  };

  const getBucketedUniqueEvents = async (
    collectionName,
    candidateTimeFields,
    startDate,
    startTimestamp,
    config,
    userFieldCandidates
  ) => {
    const labels = buildLabels(startDate, config);

    const toChart = (docs, chosenTimeField) => {
      const buckets = Array.from({ length: config.buckets }, () => new Set());
      docs.forEach((snap) => {
        const data = snap.data();
        if (shouldFilterOut(data, snap.id)) return;
        const timeField = chosenTimeField || candidateTimeFields.find((k) => data[k] != null);
        if (!timeField) return;
        const d = parseDateFromAny(data[timeField]);
        if (!d || isNaN(d.getTime())) return;
        const userField = userFieldCandidates.find((k) => data[k] != null);
        const userKey = userField ? String(data[userField]) : null;
        if (!userKey) return;
        const idx = config.index(startDate, d);
        if (idx >= 0 && idx < config.buckets) buckets[idx].add(userKey);
      });
      return labels.map((label, idx) => ({ label, value: buckets[idx].size }));
    };

    for (const field of candidateTimeFields) {
      try {
        const q = query(collection(db, collectionName), where(field, ">=", startTimestamp));
        const snapshot = await getDocs(q);
        console.log("bucketed unique fetched", {
          collectionName,
          field,
          count: snapshot.size,
          sample: snapshot.docs[0]?.data?.()[field],
        });
        return { data: toChart(snapshot.docs, field) };
      } catch (err) {
        if (err?.code === "permission-denied") {
          console.warn(`No read access to ${collectionName}:`, err);
          return { data: toChart([], null), error: "permission-denied" };
        }
        console.error("Index needed?", collectionName, field, err);
      }
    }

    try {
      const snapshot = await getDocs(query(collection(db, collectionName), limit(2000)));
      console.log("bucketed unique fallback", {
        collectionName,
        count: snapshot.size,
        sample: snapshot.docs[0]?.data?.()[candidateTimeFields[0]],
      });
      return { data: toChart(snapshot.docs, null) };
    } catch (err) {
      if (err?.code === "permission-denied") {
        console.warn(`No read access to ${collectionName}:`, err);
        return { data: toChart([], null), error: "permission-denied" };
      }
      console.error(`Failed to read ${collectionName}`, err);
      return { data: toChart([], null) };
    }
  };

  const countAllWaterings = async () => {
    const LIMIT = 5000; // safety cap; adjust if needed
    try {
      const snapshot = await getDocs(query(collection(db, "Waterings"), limit(LIMIT)));
      const count = snapshot.size;
      return count >= LIMIT ? `${count}+` : count;
    } catch (err) {
      console.error("Failed to count Waterings", err);
      return null;
    }
  };

  const bucketUsersByField = (users, startDate, config, fieldCandidates) => {
    const labels = buildLabels(startDate, config);
    const counts = Array(config.buckets).fill(0);

    users.forEach((snap) => {
      const data = snap.data();
      if (shouldFilterOut(data, snap.id)) return;

      const field = fieldCandidates.find((k) => data[k] != null);
      if (!field) return;
      const d = parseDateFromAny(data[field]);
      if (!d || isNaN(d.getTime())) return;

      const idx = config.index(startDate, d);
      if (idx >= 0 && idx < config.buckets) counts[idx]++;
    });

    return labels.map((label, idx) => ({ label, value: counts[idx] }));
  };

  const computeActiveUsersFromUsers = (users) => {
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    const daySet = new Set();
    const weekSet = new Set();
    const monthSet = new Set();

    users.forEach((snap) => {
      const data = snap.data();
      if (shouldFilterOut(data, snap.id)) return;

      const lastWater = parseDateFromAny(data.lastWateringAt);
      const lastChat = parseDateFromAny(data.lastChatAt);
      const lastActive =
        lastWater && lastChat
          ? new Date(Math.max(lastWater.getTime(), lastChat.getTime()))
          : lastWater || lastChat;

      if (!lastActive || isNaN(lastActive.getTime())) return;

      const age = now.getTime() - lastActive.getTime();
      if (age <= DAY_MS) daySet.add(snap.id);
      if (age <= 7 * DAY_MS) weekSet.add(snap.id);
      if (age <= 30 * DAY_MS) monthSet.add(snap.id);
    });

    const dau = daySet.size;
    const wau = weekSet.size;
    const mau = monthSet.size;
    const stickiness = wau > 0 ? Number((dau / wau).toFixed(2)) : 0;

    return {
      dau,
      wau,
      mau,
      stickiness,
      weeklyAvgActions: null, // unknown without per-event counts
    };
  };

  const computeCohortStat = (users) => {
    if (!cohortMonth) return null;
    const [yearStr, monthStr] = cohortMonth.split("-");
    const year = Number(yearStr);
    const monthIdx = Number(monthStr) - 1;
    if (isNaN(year) || isNaN(monthIdx)) return null;
    const start = new Date(year, monthIdx, 1, 0, 0, 0, 0);
    const end = new Date(year, monthIdx + 1, 1, 0, 0, 0, 0);
    const windowMs = Number(cohortWindowDays) > 0 ? Number(cohortWindowDays) * DAY_MS : 14 * DAY_MS;
    const cutoff = Date.now() - windowMs;

    let total = 0;
    let active = 0;
    users.forEach((snap) => {
      const data = snap.data();
      const created = parseDateFromAny(data.createdAt);
      if (!created) return;
      const createdMs = created.getTime();
      if (createdMs < start.getTime() || createdMs >= end.getTime()) return;
      total++;
      const lastChat = parseDateFromAny(data.lastChatAt);
      const lastWater = parseDateFromAny(data.lastWateringAt);
      const lastActive =
        lastChat && lastWater
          ? new Date(Math.max(lastChat.getTime(), lastWater.getTime()))
          : lastChat || lastWater;
      if (lastActive && lastActive.getTime() >= cutoff) active++;
    });

    if (total === 0) return { total: 0, active: 0, pct: 0, label: `${year}-${monthStr}` };
    return {
      total,
      active,
      pct: Math.round((active / total) * 100),
      label: `${year}-${monthStr}`,
    };
  };

  const computeRetentionFromUsers = (users) => {
    const RETENTION_WEEKS = 8;
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);
    const dow = startDate.getDay();
    startDate.setDate(startDate.getDate() - dow - (RETENTION_WEEKS - 1) * 7);

    const cohorts = Array.from({ length: RETENTION_WEEKS }, () => ({
      size: 0,
      active: Array(RETENTION_WEEKS).fill(0),
    }));

    users.forEach((snap) => {
      const data = snap.data();
      if (shouldFilterOut(data, snap.id)) return;

      const created = parseDateFromAny(data.createdAt);
      if (!created || isNaN(created.getTime())) return;
      const cohortIdx = BUCKETS.week.index(startDate, created);
      if (cohortIdx < 0 || cohortIdx >= RETENTION_WEEKS) return;

      const lastWater = parseDateFromAny(data.lastWateringAt);
      const lastChat = parseDateFromAny(data.lastChatAt);
      const lastActive =
        lastWater && lastChat
          ? new Date(Math.max(lastWater.getTime(), lastChat.getTime()))
          : lastWater || lastChat;

      cohorts[cohortIdx].size += 1;

      if (lastActive && !isNaN(lastActive.getTime())) {
        const activeIdx = BUCKETS.week.index(startDate, lastActive);
        const offset = activeIdx - cohortIdx;
        if (offset >= 0 && offset < RETENTION_WEEKS) {
          cohorts[cohortIdx].active[offset] += 1;
        }
      }
    });

    const labels = Array.from({ length: RETENTION_WEEKS }, (_, idx) =>
      BUCKETS.week.labelFor(startDate, idx)
    );

    return cohorts
      .map((c, idx) => {
        if (c.size === 0) return null;
        return {
          cohortLabel: labels[idx],
          size: c.size,
          weeks: c.active.map((val) => (c.size > 0 ? Math.round((val / c.size) * 100) : 0)),
        };
      })
      .filter(Boolean);
  };

  const computeRecentActive = (users) => {
    const items = [];
    users.forEach((snap) => {
      const data = snap.data();
      if (shouldFilterOut(data, snap.id)) return;
      const lastChat = parseDateFromAny(data.lastChatAt);
      const lastWater = parseDateFromAny(data.lastWateringAt);
      const lastActive =
        lastChat && lastWater
          ? new Date(Math.max(lastChat.getTime(), lastWater.getTime()))
          : lastChat || lastWater;
      const created = parseDateFromAny(data.createdAt);
      if (!lastActive || !created) return;
      const name = data.name || data.displayName || "";
      const email = data.email || "";
      const hasPlants = Array.isArray(data.ownedPlants) && data.ownedPlants.length > 0;
      items.push({ id: snap.id, lastActive, created, name, email, hasPlants });
    });

    // Do not sort here; sort later based on user choice
    const fmt = (d) => `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
    const rows = items.map((item) => {
      const daysActive = Math.max(
        0,
        Math.ceil((item.lastActive.getTime() - item.created.getTime()) / DAY_MS)
      );
      return {
        id: item.id,
        name: item.name,
        email: item.email,
        lastActiveLabel: fmt(item.lastActive),
        createdLabel: fmt(item.created),
        daysActive,
        lastActiveTs: item.lastActive.getTime(),
        hasPlants: item.hasPlants,
      };
    });

    const avgDaysActive =
      rows.length > 0
        ? Math.round(rows.reduce((sum, r) => sum + r.daysActive, 0) / rows.length)
        : null;
    const withPlants = rows.filter((r) => r.hasPlants);
    const avgDaysActiveWithPlants =
      withPlants.length > 0
        ? Math.round(withPlants.reduce((sum, r) => sum + r.daysActive, 0) / withPlants.length)
        : null;

    return { rows, avgDaysActive, avgDaysActiveWithPlants };
  };

  useEffect(() => {
    if (usersCache.length > 0) {
      recomputeDerivedFromUsers(usersCache);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdAfter, createdBefore, activeSinceDays, excludeNoPlants, cohortMonth, cohortWindowDays]);

  const backfillCreatedAt = async () => {
    if (backfillState.running) return;
    if (
      !window.confirm(
        backfillUserId.trim()
          ? `Backfill createdAt for user ${backfillUserId.trim()} from Auth creation time?`
          : "Backfill createdAt for all users missing it, using Auth creation time? This will write to user docs."
      )
    )
      return;
    setBackfillState((s) => ({ ...s, running: true, message: "Running…", updated: 0, skipped: 0, failed: 0 }));
    try {
      const payload = backfillUserId.trim() ? { userId: backfillUserId.trim() } : {};
      const response = await fetch(`${API_BASE}/backfillCreatedAtFromAuth`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `HTTP ${response.status}`);
      }
      const result = await response.json();
      setBackfillState({
        running: false,
        message: `Backfill done (${result.target || "all"}): ${result.updated} set, ${result.skipped} already had createdAt, ${result.failed || 0} failed, ${result.missing || 0} missing creationTime.`,
        updated: result.updated,
        skipped: result.skipped,
        failed: result.failed || 0,
      });
    } catch (err) {
      console.error("Backfill error", err);
      setBackfillState((s) => ({
        ...s,
        running: false,
        message: "Backfill error (see console).",
      }));
    }
  };

  const config = BUCKETS[period] || BUCKETS.week;

  return (
    <div>
      <ConsoleHomePageHeader />
      <div className="title">Analytics Dashboard</div>
      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "20px" }}>
        <div className="analytics-toolbar">
          <div className="field">
            <label className="plantTextFieldLabel">Filter out ID</label>
            <input
              className="textbox plantIDTextbox"
              type="text"
              placeholder="Enter plant or user ID to exclude"
              value={filterId}
              onChange={(e) => setFilterId(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="plantTextFieldLabel">Period</label>
            <select
              className="textbox plantIDTextbox"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="day">Daily (30d)</option>
              <option value="week">Weekly (12w)</option>
              <option value="month">Monthly (12m)</option>
            </select>
          </div>
          <div className="field">
            <label className="plantTextFieldLabel">One-time backfill</label>
            <input
              className="textbox plantIDTextbox"
              type="text"
              placeholder="Optional user ID to backfill a single user"
              value={backfillUserId}
              onChange={(e) => setBackfillUserId(e.target.value)}
              style={{ marginBottom: 6 }}
            />
            <button
              className="textbox plantIDTextbox"
              style={{ cursor: backfillState.running ? "not-allowed" : "pointer" }}
              onClick={backfillCreatedAt}
              disabled={backfillState.running}
            >
              {backfillState.running ? "Backfilling..." : "Set missing createdAt"}
            </button>
            {backfillState.message ? (
              <div className="retention-hint" style={{ marginTop: 4 }}>{backfillState.message}</div>
            ) : null}
          </div>
        </div>

        <div className="analytics-toolbar">
          <div className="field">
            <label className="plantTextFieldLabel">Created after</label>
            <input
              className="textbox plantIDTextbox"
              type="date"
              value={createdAfter}
              onChange={(e) => setCreatedAfter(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="plantTextFieldLabel">Created before</label>
            <input
              className="textbox plantIDTextbox"
              type="date"
              value={createdBefore}
              onChange={(e) => setCreatedBefore(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="plantTextFieldLabel">Active since (days)</label>
            <input
              className="textbox plantIDTextbox"
              type="number"
              min="0"
              placeholder="e.g. 14"
              value={activeSinceDays}
              onChange={(e) => setActiveSinceDays(e.target.value)}
            />
          </div>
          <div className="field" style={{ paddingTop: 18 }}>
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={excludeNoPlants}
                onChange={(e) => setExcludeNoPlants(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              Exclude users with no owned plants
            </label>
          </div>
        </div>

        <div className="metric-grid">
          <div className="metric-card">
            <div className="metric-label">
              DAU <InfoTooltip text="Distinct users with activity in the last 24h (based on lastWateringAt or lastChatAt)." />
            </div>
            <div className="metric-value">{topline.dau}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">
              WAU <InfoTooltip text="Distinct users active in the last 7 days (based on lastWateringAt or lastChatAt)." />
            </div>
            <div className="metric-value">{topline.wau}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">
              MAU <InfoTooltip text="Distinct users active in the last 30 days (based on lastWateringAt or lastChatAt)." />
            </div>
            <div className="metric-value">{topline.mau}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">
              Stickiness (DAU/WAU){" "}
              <InfoTooltip text="How often weekly actives show up daily. Calculated as DAU divided by WAU." />
            </div>
            <div className="metric-value">
              {topline.stickiness ? `${Math.round(topline.stickiness * 100)}%` : "0%"}
            </div>
            <div className="metric-subtext">Closer to 100% is better</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">
              Avg actions / active (7d){" "}
              <InfoTooltip text="Average events per active user over the last 7 days. Not computed without per-event counts." />
            </div>
            <div className="metric-value">
              {topline.weeklyAvgActions == null ? "—" : topline.weeklyAvgActions}
            </div>
            <div className="metric-subtext">Events needed to compute</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">
              Total Waterings{" "}
              <InfoTooltip text="Approx. all-time count of Waterings documents (capped fetch; shows 5000+ if above cap)." />
            </div>
            <div className="metric-value">{totals.waterings == null ? "…" : totals.waterings}</div>
          </div>
        </div>
        {permError.activity ? (
          <div style={{ color: "#b00", marginBottom: 8 }}>
            You may not have permission to read some collections for activity metrics.
          </div>
        ) : null}

        <h3>
          Waterings (events) — Last {config.buckets} {config.label}{" "}
          <InfoTooltip text="Total watering events per bucket using event timestamps (e.g., wateredAt/createdAt)." />
        </h3>
        <SimpleBarChart data={wateringData} />

        <h3>
          Unique Waterers — Last {config.buckets} {config.label}{" "}
          <InfoTooltip text="Distinct users who watered inside each bucket, based on Waterings userId and event timestamp." />
        </h3>
        <SimpleBarChart data={watererData} />

        <h3>
          Active Chatters (unique) — Last {config.buckets} {config.label}{" "}
          <InfoTooltip text="Distinct users who chatted inside each bucket, based on Chats userId and event timestamp." />
        </h3>
        <SimpleBarChart data={chatData} />

        <h3>
          New Users (by createdAt) — Last {config.buckets} {config.label}{" "}
          <InfoTooltip text="Users grouped by their createdAt timestamp. Counts signups per bucket." />
        </h3>
        <SimpleBarChart data={plantData} />

        <h3 style={{ marginTop: "32px" }}>
          Retention cohorts (Users){" "}
          <InfoTooltip text="Cohorts by user.createdAt. W0 is signup week; later columns show if the user was active (lastWateringAt/lastChatAt) in that week." />
        </h3>
        <div className="retention-hint">
          Cohort from user.createdAt; activity from lastWateringAt/lastChatAt. W0 is signup week.
        </div>
        <RetentionTable data={retentionData} />

        <h3 style={{ marginTop: "24px" }}>
          Cohort activity snapshot{" "}
          <InfoTooltip text="Pick a created month and window to see what percent of that cohort was active in the last X days." />
        </h3>
        <div className="analytics-toolbar" style={{ marginBottom: 8 }}>
          <div className="field">
            <label className="plantTextFieldLabel">Cohort month</label>
            <input
              className="textbox plantIDTextbox"
              type="month"
              value={cohortMonth}
              onChange={(e) => setCohortMonth(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="plantTextFieldLabel">Active window (days)</label>
            <input
              className="textbox plantIDTextbox"
              type="number"
              min="1"
              value={cohortWindowDays}
              onChange={(e) => setCohortWindowDays(e.target.value)}
            />
          </div>
          <div className="field" style={{ alignSelf: "flex-end" }}>
            {cohortStat ? (
              <div className="retention-hint">
                {cohortStat.label}: {cohortStat.active}/{cohortStat.total} active in last{" "}
                {cohortWindowDays || 14}d ({cohortStat.pct}%)
              </div>
            ) : (
              <div className="retention-hint">Pick a month to compute activity.</div>
            )}
          </div>
        </div>

        <h3 style={{ marginTop: "24px" }}>
          Recent active users (most recent chat/watering){" "}
          <InfoTooltip text="Sorted by last chat or watering (whichever is newer). Shows createdAt, days active, and user identity. Users missing createdAt are omitted." />
        </h3>
        <div className="analytics-toolbar" style={{ marginTop: 8, marginBottom: 8 }}>
          <div className="field">
            <label className="plantTextFieldLabel">Sort</label>
            <select
              className="textbox plantIDTextbox"
              value={recentSort}
              onChange={(e) => setRecentSort(e.target.value)}
            >
              <option value="recent">Most recently active</option>
              <option value="longest">Longest days active</option>
            </select>
          </div>
          <div className="field" style={{ minWidth: 220 }}>
            {recentAvgDaysActive != null ? (
              <div className="retention-hint">
                Avg days active (shown): {recentAvgDaysActive}
                {recentAvgDaysActiveWithPlants != null
                  ? ` | With plants: ${recentAvgDaysActiveWithPlants}`
                  : ""}
              </div>
            ) : (
              <div className="retention-hint">No data for averages.</div>
            )}
          </div>
        </div>
        <div className="recent-list">
          {recentChatters.length === 0 ? (
            <div className="retention-empty">
              No recent chatters or waterers with createdAt available.
            </div>
          ) : (
            [...recentChatters]
              .sort((a, b) =>
                recentSort === "longest"
                  ? b.daysActive - a.daysActive
                  : b.lastActiveTs - a.lastActiveTs
              )
              .slice(0, 50)
              .map((item) => (
              <div key={item.id} className="recent-row">
                <span className="recent-id">{item.name || item.email || item.id.slice(0, 8)}</span>
                {item.email ? <span className="recent-meta">{item.email}</span> : null}
                <span className="recent-date">Created: {item.createdLabel}</span>
                <span className="recent-date">Last active: {item.lastActiveLabel}</span>
                <span className="recent-date">Days active: {item.daysActive}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
